import { mapDesigns } from "@mono/mapDesigns"
import { allPieces } from "@/utils/data"
import { getUserCreds } from "@/utils/utils"

/**
 * The made-up game the guide is a picture of (see Modals/GuideModal.tsx).
 *
 * The guide does not draw its own copy of the screen any more — it renders the
 * real game screen (Board, both sidebars, every panel) against this state, in
 * its own store, at the real viewport size, and then scales the whole thing
 * down to fit the modal. So the diagram is the game, not a drawing of it, and
 * it cannot drift as the game's own components change.
 *
 * What is in here is therefore not decoration: every value exists to put one
 * more part of the screen on it. A completed colour set so the houses have
 * somewhere to stand and the build buttons have something to say; a construction
 * ban so that badge is up; a dropped player so the takeover countdown is on a
 * card; offers on the table so the trades panel has rows; a finished auction so
 * the "last auction" button exists at all. A real game reaches these one at a
 * time; the guide needs them all at once.
 *
 * Three things a real screen never shows together are settled here rather than
 * faked, because the mock is a real render and cannot cheat:
 *
 *   - it is *your* turn, so the turn buttons and a finished roll are what the
 *     middle of the board holds, rather than the "[player] is playing" line;
 *   - nobody has a fast-pace vote open, so the propose button is the thing in
 *     that slot rather than the vote panel — and the propose button is the one
 *     the guide labels;
 *   - one seat is a disconnected human, which is what puts the takeover strip on
 *     screen. That also hides the Pause key, which is only offered when every
 *     other seat is a bot: a copy of the screen can only be one of the two, and
 *     the dropped seat is the one worth showing.
 *
 * The map is the one the viewer is actually playing, so the board in the guide
 * is the board they are looking at.
 */

// The seat the viewer is sitting in. Fixed at 0 so the selectors below can name
// their targets ("#player-list-item-1") without threading orders around.
const ME: PlayerOrder = 0

const emptyStats = { rating: 1200, gamesPlayed: 0, gamesWon: 0 }

const mockUser = (email: string, username: string, role: GameRoleId): IUser =>
  ({
    email,
    username,
    role,
    profileImage: "",
    stats: emptyStats,
  } as IUser)

const mockPlayer = (player: Partial<IPlayer> & Pick<IPlayer, "id" | "order">): IPlayer =>
  ({
    money: 1500,
    properties: [],
    pieceId: "0",
    position: 0,
    eliminated: false,
    rollHistory: [],
    inJail: false,
    inJailTurns: 0,
    jailFreeCards: [],
    isBankrupt: false,
    boughtPropertiesWithoutAuction: 0,
    canBuyPropertiesWithoutAuction: 0,
    cards: [],
    stock: 0,
    role: "none",
    stockMultiplier: 1,
    turnsBeingBankrupt: 0,
    cardsPlayed: 0,
    ...player,
  } as IPlayer)

const card = (cardId: ActionCardId, n: number): IPlayerActionCard => ({
  cardId,
  id: `guide-card-${n}`,
  playable: true,
})

/** Property orders belonging to `groupId`, in board order. */
const groupOrders = (map: IMapDesign, groupId: string): number[] =>
  map.cells
    .filter((cell) => cell.propertyDetails?.groupId === groupId)
    .map((cell) => cell.order)

export interface IGuideMock {
  /** Everything the mock screen renders from, for the guide's own store. */
  state: Partial<IGlobalState>
  /**
   * One CSS selector per help spot id, resolved inside the mock's own root.
   *
   * The DOM coupling lives here rather than in helpSpots.ts because half of
   * these depend on the game above — which tile got the houses, which seat
   * dropped out — and only this file knows that. helpSpots.ts is left holding
   * nothing but the words. A spot with no selector, or a selector that matches
   * nothing on the day, is simply left off the diagram (see GuideModal).
   */
  selectors: Record<string, string>
}

export const buildGuideMock = (
  mapId: string | undefined,
  theme: IGlobalState["selectedTheme"]
): IGuideMock => {
  const map = (mapDesigns.find((item) => item.id === mapId) ||
    mapDesigns[0]) as IMapDesign

  // ------------------------------------------------------------------
  // Pick the tiles the board-tile labels point at out of the map itself,
  // rather than hard-coding orders: the two maps number their tiles
  // differently, and the guide has to point at the right one on both.
  // ------------------------------------------------------------------
  const corners = map.cells.filter((cell) => cell.type === "custom")
  const goOrder = corners[0]?.order ?? 0
  // The other custom corner is the steal-tax tile — the one that pays out the
  // pile in the middle.
  const stealTaxOrder = corners[corners.length - 1]?.order ?? 20
  const actionOrder = map.cells.find((cell) => cell.type === "action")?.order ?? 0
  const taxOrder = map.cells.find((cell) => cell.type === "tax")?.order ?? 0
  // Both maps put a decision hub in two of the four corners. The label goes on
  // the second one: the first shares its corner of the diagram with the board's
  // bottom row and the left column's labels, and the second has the empty strip
  // above the board to lean into.
  const hubOrders = map.cells
    .filter((cell) => cell.type === "decision-hub")
    .map((cell) => cell.order)
  const hubOrder = hubOrders[1] ?? hubOrders[0] ?? 0

  // Colour sets, in board order. The first is left alone (its tiles are the
  // cheap ones players meet first and make the clearest "unowned property"
  // example); the rest are dealt out below.
  const buildableGroups = (map.groups || [])
    .filter((group) => group.withStrip && group.housePrice)
    .map((group) => groupOrders(map, group.id))
    .filter((orders) => orders.length > 1)

  const [firstSet, mySet, priyaSet, marcoSet, danaSet] = buildableGroups
  // The set the viewer owns outright — that is what makes the build buttons
  // meaningful and gives the houses a home.
  const mine = mySet || []
  const housedOrder = mine[0]
  // An unowned tile, kept clear of pieces: it is the plain example of a
  // property, next to the owned one the owner-marker label points at.
  const unownedOrder = (firstSet || [])[1] ?? (firstSet || [])[0]

  const priyaProps = (priyaSet || []).slice(0, 2)
  const danaProps = (danaSet || []).slice(0, 2)

  // The tile the owner-marker label points at, picked by name rather than by
  // position in a set. The marker is a faded piece printed down the middle of
  // the tile, so it wants one whose middle is otherwise empty and which stands
  // in a side column, where its label can lean out over the sidebar instead of
  // across its neighbours. Canada is that tile on both maps; if a map ever
  // drops it, the second of Marco's tiles is the same shape of answer.
  const namedOrder = map.cells.find(
    (cell) => cell.propertyDetails?.name === "Canada"
  )?.order
  const alreadyOwned = [...mine, ...priyaProps, ...danaProps]
  const ownerMarkerOrder =
    namedOrder !== undefined && !alreadyOwned.includes(namedOrder)
      ? namedOrder
      : (marcoSet || [])[1] ?? (marcoSet || [])[0]

  // Marco is the opponent holding it, so the marker on it is his piece.
  const marcoProps = Array.from(
    new Set(
      [...(marcoSet || []).slice(0, 2), ownerMarkerOrder].filter(
        (order) => order !== undefined
      )
    )
  )

  // Pieces stand somewhere harmless: a token parked on a labelled tile would
  // sit under the ring drawn around it.
  const taken = [
    goOrder,
    stealTaxOrder,
    actionOrder,
    taxOrder,
    hubOrder,
    unownedOrder,
    housedOrder,
    ownerMarkerOrder,
  ]
  const standingRoom = map.cells
    .filter((cell) => cell.propertyDetails && !taken.includes(cell.order))
    .map((cell) => cell.order)
  const stand = (n: number) => standingRoom[n % standingRoom.length] ?? goOrder

  const myEmail = getUserCreds().email || "you@guide.local"
  const users: IUser[] = [
    mockUser(myEmail, "You", "rent-lord"),
    mockUser("guide-priya@guide.local", "Priya", "auction-strategist"),
    mockUser("guide-marco@guide.local", "Marco", "the-constructor"),
    mockUser("guide-dana@guide.local", "Dana", "financer"),
  ]

  const players: IPlayer[] = [
    mockPlayer({
      id: users[0].email,
      order: ME,
      money: 1480,
      pieceId: allPieces[0].id,
      position: stand(0),
      properties: [...mine],
      role: "rent-lord",
      cards: [card("get-10-percent-from-player", 1), card("apply-house-tax", 2), card("say-no", 3)],
      rollHistory: [],
    }),
    mockPlayer({
      id: users[1].email,
      order: 1,
      money: 920,
      pieceId: allPieces[1].id,
      position: stand(3),
      properties: priyaProps,
      role: "auction-strategist",
      cards: [card("forced-auction", 4)],
    }),
    mockPlayer({
      id: users[2].email,
      order: 2,
      money: 1105,
      pieceId: allPieces[2].id,
      position: stand(6),
      properties: marcoProps,
      role: "the-constructor",
      bot: true,
    }),
    // The seat the takeover countdown is running on: dropped, and still human —
    // a bot seat has somebody playing it, so it never shows the strip.
    mockPlayer({
      id: users[3].email,
      order: 3,
      money: 640,
      pieceId: allPieces[3].id,
      position: stand(9),
      properties: danaProps,
      role: "financer",
      disconnected: true,
    }),
  ]

  const now = Date.now()
  const auctionedOrder = (marcoSet || [])[0] ?? unownedOrder

  const situation: IGameSituation = {
    players,
    turn: myEmail,
    turnsLeft: 12,
    historyItems: [
      {
        type: "auction-won",
        propertyOrder: auctionedOrder,
        playerOrder: 2,
        amount: 180,
        auctionBids: { 0: 120, 1: 150, 2: 180, 3: 0 } as Record<PlayerOrder, number>,
      },
      { type: "rent-paid", propertyOrder: housedOrder, playerOrder: 1, ownerOrder: ME, rent: 120 },
      { type: "house-bought", playerOrder: ME, propertyOrder: housedOrder, count: 2 },
      { type: "card-played", cardId: "apply-property-tax", playerOrder: 3, cardType: "chance", targetedPlayerOrder: ME, amount: 200 },
      { type: "steal-tax", playerOrder: 1, amount: 340 },
    ] as IHistoryItem[],
    rollEnabled: true,
    endTurnEnabled: true,
    chanceCardsOrder: [],
    communityChestCardOrder: [],
    recentlyDrawnChanceId: "",
    recentlyDrawnCommunityChestId: "",
    // Never undefined: a live auction blanks the roll button and dims the whole
    // board centre.
    activeAuctionPropertyOrder: null,
    events: [],
    mortgagedProperties: [],
    housesData: [
      { propertyOrder: mine[0], houses: 2 },
      { propertyOrder: mine[1], houses: 1 },
    ].filter((item) => item.propertyOrder !== undefined),
    endGameCardsDrawn: 0,
    gameOver: false,
    // The board's "no building" badge. It greys the build button too, which is
    // the honest picture: both are labelled, and the ban is what explains it.
    constructionBanned: true,
    turnRemainingForConstructionBan: 3,
    pickingRoles: false,
    pickedRoles: {},
    moneyOnTable: 640,
    activeTrades: [
      {
        id: "guide-trade-1",
        offeredAt: now - 18000,
        offeringPlayerOrder: 1,
        offeredPlayerOrder: ME,
        givenProperties: priyaProps.slice(0, 1),
        takenProperties: mine.slice(2, 3),
        moneyGiven: 0,
        moneyTaken: 150,
      },
      {
        id: "guide-trade-2",
        offeredAt: now - 42000,
        offeringPlayerOrder: ME,
        offeredPlayerOrder: 2,
        givenProperties: mine.slice(2, 3),
        takenProperties: marcoProps.slice(0, 1),
        moneyGiven: 200,
        moneyTaken: 0,
      },
    ],
    completedTrades: [
      {
        id: "guide-trade-0",
        completedAt: now - 240000,
        offeringPlayerOrder: 3,
        offeredPlayerOrder: 1,
        givenProperties: danaProps.slice(0, 1),
        takenProperties: [],
        moneyGiven: 0,
        moneyTaken: 260,
      },
    ],
    rejectedTrades: [],
    doubleRentProperties: [],
    disabledProperties: [],
    cardsInDeck: {} as IGameSituation["cardsInDeck"],
    // No vote on the table, so the slot at the head of the left column holds the
    // propose button rather than the vote panel.
    fastPaceVote: null,
  }

  const game: IGameDetails = {
    id: "guide-mock-game",
    host: myEmail,
    hasStarted: true,
    isPrivate: false,
    password: "",
    // No challenge: the badge it puts in the bottom-right corner is not part of
    // the guide, and an unlabelled card sitting under the trades panel is one
    // more thing on a screen that is already busy.
    players: users,
    settings: {
      mapId: map.id,
      maxPlayers: 4,
      minPlayers: 2,
      noOfPlayers: 4,
      maxTurns: 40,
      startingFunds: 1500,
      title: "Guide",
      endGameCardsNeeded: 4,
      roles: {},
    },
    situation,
    timers: { turnTimeLeft: 21 },
  }

  const state: Partial<IGlobalState> = {
    myGame: game,
    myGameId: game.id,
    selectedTheme: theme,
    // The countdown the takeover strip on Dana's card is counting down.
    botTakeoverCountdowns: { 3: 38 },
    // The badge under the turn buttons. It is our own turn here, so it reads as
    // the game about to act for us.
    idleWarning: { playerOrder: ME, secondsLeft: 6, action: "end-turn" },
    // End Turn is drawn live rather than greyed: the local lock is what a real
    // client sets between its own turns.
    disableEndTurnLocally: false,
    diceRolling: false,
    pieceMovementAnimationRunning: false,
    isGamePaused: false,
    houseBuildMode: null,
    boardPickerMode: null,
    pendingDecision: null,
    helpTourActive: false,
  }

  const cellSelector = (order: number) => `[data-testid="game.cell-${order}"]`

  // Only the parts the guide has something non-obvious to say about are in
  // here. The rest of the screen is drawn and left alone — see the note at the
  // top of helpSpots.ts.
  const selectors: Record<string, string> = {
    // Board tiles.
    "go-tile": cellSelector(goOrder),
    "owner-marker": `[data-testid="game.cell-owner-${ownerMarkerOrder}"]`,
    "house-marker": `[data-testid="game.cell-strip-${housedOrder}"]`,
    "steal-tax-tile": cellSelector(stealTaxOrder),
    "decision-hub-tile": cellSelector(hubOrder),

    // The middle of the board.
    "turn-timer": '[data-testid="game.turn-timer"]',
    "turns-left": '[data-testid="game.turns-left-badge"]',
    "money-on-table": '[data-testid="game.money-on-table"]',
    "idle-countdown": '[data-testid="game.auto-end-turn-badge"]',

    // The two side columns.
    "fast-pace": '[data-testid="game.propose-fast-pace-button"]',
    "role-badge": '[data-testid="game.player-role-badge-1"]',
    "last-auction": '[data-testid="game.last-auction-result-button"]',
    "create-trade": '[data-testid="game.trade-btn"]',
    bank: "#player-list-item-100",
    "trades-panel": "#active-trades-panel",

    // The keys in the top-right corner.
    "music-toggle": '[data-testid="game.background-music-toggle"]',
    "settings-button": '[data-testid="game.settings-button"]',
  }

  return { state, selectors }
}
